import logging from 'domain/logging';

class PerformanceTracker {
  constructor() {
    this.dataLayer = window.dataLayer || [];
    this.store = null; // Will be set by app initialization

    // Check if Performance API is available
    this.hasPerformanceAPI =
      typeof performance !== 'undefined' &&
      performance.mark &&
      performance.measure &&
      performance.getEntriesByName;
  }

  /**
   * Set Redux store reference to enable automatic channel detection
   * Should be called once during app initialization
   * @param {Object} store - Redux store instance
   */
  setStore(store) {
    this.store = store;
  }

  /**
   * Get current channel from Redux store
   * @returns {string|undefined} Current channel or undefined
   */
  getCurrentChannel() {
    try {
      if (this.store) {
        const state = this.store.getState();
        return state?.settings?.report?.tab;
      }
    } catch (error) {
      // Silently fail if store is not available
    }
    return undefined;
  }

  /**
   * Get user information from Redux store
   * @returns {Object} User info with userId and accountId
   */
  getUserInfo() {
    try {
      if (this.store) {
        const state = this.store.getState();
        const userId = state?.user?.userId;
        const accountId = state?.user?.account?.accountId;

        return {
          ...(userId && { userId }),
          ...(accountId && { accountId }),
        };
      }
    } catch (error) {
      // Silently fail if store is not available
    }
    return {};
  }

  /**
   * Get detailed channel filter (集計チャネル) from Redux store
   * @returns {string|undefined} Detailed channel filter values (e.g., "クリック+自然検索+ダイレクト")
   */
  getDetailedChannel() {
    try {
      if (this.store) {
        const state = this.store.getState();
        const filters = state?.settings?.filters?.items;

        // Check for both singular and plural keys
        const channelFilter = filters?.channel_access_type || filters?.channel_access_types;

        if (channelFilter && channelFilter.values && channelFilter.values.length > 0) {
          return channelFilter.values.join(',');
        }
      }
    } catch (error) {
      // Silently fail
    }
    return undefined;
  }

  /**
   * Get reporting period from Redux store
   * @returns {Object|undefined} Period object with startDate and endDate (e.g., {periodStart: "2024-01-01", periodEnd: "2024-01-31"})
   */
  getReportingPeriod() {
    try {
      if (this.store) {
        const state = this.store.getState();
        const period = state?.commonState?.CommonState?.settings?.period;

        if (period && period.start && period.end) {
          // period.start and period.end are moment objects, format them as strings
          return {
            periodStart: period.start.format ? period.start.format('YYYY-MM-DD') : period.start,
            periodEnd: period.end.format ? period.end.format('YYYY-MM-DD') : period.end,
          };
        }
      }
    } catch (error) {
      // Silently fail
    }
    return undefined;
  }

  /**
   * Check if any filters are currently applied
   * @returns {boolean} True if at least one filter has values selected
   */
  hasFilters() {
    try {
      if (this.store) {
        const state = this.store.getState();
        const filters = state?.settings?.filters?.items;

        if (filters && typeof filters === 'object') {
          // Check if any filter has values
          return Object.values(filters).some(filter => {
            if (filter && filter.values && Array.isArray(filter.values)) {
              return filter.values.length > 0;
            }
            if (filter && filter.ids && Array.isArray(filter.ids)) {
              return filter.ids.length > 0;
            }
            return false;
          });
        }
      }
    } catch (error) {
      // Silently fail
    }
    return false;
  }

  /**
   * Mark the start of screen loading using Performance API
   * @param {string} screenId - Screen identifier (e.g., 'category-analyze')
   */
  startTracking(screenId) {
    if (!this.hasPerformanceAPI) {
      logging.warn('[Performance] Performance API not available');
      return;
    }

    const markName = `start-${screenId}`;

    // Check if already tracking this screen (avoid duplicate logs)
    const existingMarks = performance.getEntriesByName(markName);
    const isAlreadyTracking = existingMarks.length > 0;

    // Clear any existing marks with the same name
    try {
      performance.clearMarks(markName);
    } catch (e) {
      // Ignore if mark doesn't exist
    }

    // Create performance mark
    performance.mark(markName);

    // Only log if this is the first time tracking this screen
    if (!isAlreadyTracking) {
      logging.info(`[Performance] Started tracking: ${screenId}`);
    }
  }

  /**
   * Mark the end of screen loading, create measure, and send timing to GTM
   * @param {string} screenId - Screen identifier
   * @param {Object} metadata - Additional metadata (optional)
   */
  endTracking(screenId, metadata = {}) {
    if (!this.hasPerformanceAPI) {
      logging.warn('[Performance] Performance API not available');
      return;
    }

    const startMarkName = `start-${screenId}`;
    const endMarkName = `end-${screenId}`;
    const measureName = `load-${screenId}`;

    // Check if start mark exists
    const startMarks = performance.getEntriesByName(startMarkName);
    if (startMarks.length === 0) {
      // Silently skip if no start mark (common for cached/instant loads)
      return;
    }

    // Create end mark
    performance.mark(endMarkName);

    // Create measure between start and end marks
    try {
      performance.measure(measureName, startMarkName, endMarkName);

      // Get the measure
      const measures = performance.getEntriesByName(measureName);
      const measure = measures[measures.length - 1]; // Get most recent measure

      const loadingTime = Math.round(measure.duration);

      // Automatically get current channel and user info if available
      const channel = this.getCurrentChannel();
      const userInfo = this.getUserInfo();
      const detailedChannel = this.getDetailedChannel();
      const reportingPeriod = this.getReportingPeriod();
      const hasFilters = this.hasFilters();

      // Send to GTM dataLayer
      this.dataLayer.push({
        event: 'screenLoadComplete',
        screenId,
        loadingTime, // in milliseconds
        ...(channel && { channel }), // Include channel if available
        ...(detailedChannel && { detailedChannel }), // Include detailed channel if available
        ...reportingPeriod, // Include periodStart and periodEnd if available
        hasFilters, // Boolean flag indicating if filters are applied
        ...userInfo, // Include userId and accountId if available
        ...metadata, // User-provided metadata can override
      })

      // Build log message with optional channel (concise format)
      const channelSuffix = channel ? ` (${channel})` : '';
      logging.info(`[Performance] ${screenId}${channelSuffix} loaded in ${loadingTime}ms`);

      // Clean up marks and measures to avoid memory leaks
      performance.clearMarks(startMarkName);
      performance.clearMarks(endMarkName);
      performance.clearMeasures(measureName);

    } catch (error) {
      logging.error(`[Performance] Error creating measure for ${screenId}:`, error);
    }
  }

  /**
   * Track API request timing using Performance Resource Timing
   * @param {string} endpoint - API endpoint name
   * @param {number} duration - Request duration in ms
   * @param {string} screenId - Associated screen ID
   */
  trackApiRequest(endpoint, duration, screenId) {
    this.dataLayer.push({
      event: 'apiRequestComplete',
      endpoint,
      duration,
      screenId,
    });
  }

  /**
   * Get all screen loading measures
   * @returns {Array} Array of performance measures
   */
  getAllScreenMeasures() {
    if (!this.hasPerformanceAPI) return [];

    return performance.getEntriesByType('measure')
      .filter(entry => entry.name.startsWith('load-'));
  }

  /**
   * Get specific screen loading measure
   * @param {string} screenId - Screen identifier
   * @returns {PerformanceMeasure|null} Performance measure or null
   */
  getScreenMeasure(screenId) {
    if (!this.hasPerformanceAPI) return null;

    const measureName = `load-${screenId}`;
    const measures = performance.getEntriesByName(measureName);
    return measures.length > 0 ? measures[measures.length - 1] : null;
  }
}

// Singleton instance
const performanceTracker = new PerformanceTracker();

export default performanceTracker;
